/**
 * Calm v2 design tokens — the redesign system ("NEW" / "CNEW" in the design
 * handoff bundles). Single source of truth for the calm-v2 palette, the CRM
 * deal-stage colors, and the shared shape/motion scale.
 *
 * ADDITIVE AND OPT-IN. Nothing here changes existing apps:
 * - TS consumers import these named exports explicitly.
 * - CSS/Tailwind consumers must `@import '../../../packages/theme/src/v2/calm.css'`
 *   in their app `index.css` (after design-system.css). Apps that do not import
 *   it (quotes, portal, team) are unaffected.
 *
 * The values here MUST stay in sync with the `@theme` block in `./calm.css`.
 */

/** Calm v2 color palette (the handoff "NEW" object, key-for-key). */
export const calmColors = {
  /** App background. CSS/Tailwind name: `page` (e.g. `bg-page`). */
  bg: '#F6F7F9',
  /** Cards, tables, sidebar, top bar. */
  surface: '#FFFFFF',
  /** Table header rows, input backgrounds. */
  surfaceSoft: '#FAFBFC',
  /** Primary text — near-black with a blue undertone. */
  ink: '#0B1020',
  /** Secondary text. */
  ink2: '#2B3446',
  /** Labels, meta text. */
  muted: '#6B7280',
  /** Tertiary text, timestamps. */
  sub: '#9AA3B2',
  /** Default 1px borders. */
  line: '#E6E8EE',
  /** Input borders, emphasized dividers. */
  lineStrong: '#D5D9E0',
  /** Primary actions, active nav, links. */
  brand: '#1D3A8A',
  /** Dark brand panels (login hero) and primary-button hover. */
  brandInk: '#0F2260',
  /** Active-nav background, assignment chips, soft brand fills. */
  brandSoft: '#EEF1F8',
  success: '#0E7C5A',
  successSoft: '#E6F4EF',
  warn: '#B7730B',
  warnSoft: '#FBF3E2',
  /** Text on warnSoft fills: warn itself is below 4.5:1 there. CSS/Tailwind: `warn-ink`. */
  warnInk: '#8A5600',
  danger: '#B42318',
  dangerSoft: '#FDECEA',
  info: '#1D4FD7',
  infoSoft: '#E8EEFD',
} as const;

export type CalmColorName = keyof typeof calmColors;

/**
 * CRM deal pipeline stages — the canonical declaration lives in @elka/types
 * (models.ts). Re-exported here so stage-token consumers share one identity,
 * and `Record<DealStage, StageColor>` below breaks the build if a stage is
 * ever added without a color.
 */
export type { DealStage } from '@elka/types';
import type { DealStage } from '@elka/types';

export const STAGE_ORDER: readonly DealStage[] = [
  'lead',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'lost',
];

export interface StageColor {
  /** Text color — stage labels, chip text. */
  fg: string;
  /** Soft fill — stage chip/badge background. */
  bg: string;
  /** Solid accent — 6px dots, probability bars, chart series. */
  solid: string;
}

/**
 * Single source of truth for deal-stage color (replaces the three divergent
 * definitions in crm: filter-chip classes, DealsTable badge variants, pie hexes).
 * Usage rule: tables/lists show a 6px `solid` dot + plain text; the full
 * `bg`+`fg` chip is reserved for stage rails and detail-view badges.
 */
export const stageColors: Record<DealStage, StageColor> = {
  lead: { fg: '#475467', bg: '#F2F4F7', solid: '#98A2B3' },
  qualified: { fg: '#175CD3', bg: '#EFF4FF', solid: '#2E7AE6' },
  proposal: { fg: '#5B47BD', bg: '#F0EDFB', solid: '#7B61E0' },
  negotiation: { fg: '#9B5C00', bg: '#FFF6E5', solid: '#D69013' },
  won: { fg: '#0E7C5A', bg: '#E6F4EF', solid: '#16A57A' },
  lost: { fg: '#B42318', bg: '#FDECEA', solid: '#D8453B' },
};

/**
 * Radius/shadow/focus-ring/motion values live only in `./calm.css`'s `@theme`
 * block (consumed via Tailwind utilities like `rounded-card`, `shadow-overlay`,
 * `:focus-visible`) — no TS re-export exists since nothing consumes one.
 */
